import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useCancelCustomerSubscriptionMutation } from "../../../../store/queries/Subscription";

const useCancelSubscription = (subscription, handleClose, refetch) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelSubscription] = useCancelCustomerSubscriptionMutation();

  const emptyToNull = (value, original) => (original === "" ? null : value);

  const validation = Yup.object({
    refund_amount: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .when("refund", {
        is: true,
        then: (schema) =>
          schema
            .required("Enter the amount to refund")
            .min(0, "Cannot be negative"),
      }),
    reason: Yup.string().trim().required("Enter a reason").max(500),
  });

  const formik = useFormik({
    initialValues: {
      refund: false,
      refund_amount: "",
      reason: "",
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setIsCancelling(true);

      // admin_id is not sent - the admin service stamps it from the token, so a cancellation
      // is always attributable to whoever is actually signed in.
      const response = await cancelSubscription({
        subscription_id: subscription?.subscription_id,
        refund: values?.refund,
        refund_amount: values?.refund ? Number(values?.refund_amount) : null,
        reason: values?.reason,
      });

      if (response?.data?.status_code === 200) {
        toast.success(`Subscription cancelled`);
        setIsCancelling(false);
        refetch();
        handleClose();
        return;
      }

      // A refused refund leaves the subscription untouched, so this has to read as a failure
      // rather than as a partial success.
      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to cancel this subscription`
      );
      setIsCancelling(false);
    },
  });

  /**
   * Prefills the balance's own value so the agent is arguing with the plan's figure rather
   * than inventing one, while leaving it editable.
   */
  useEffect(() => {
    if (formik.values.refund && formik.values.refund_amount === "") {
      formik.setFieldValue(
        "refund_amount",
        subscription?.outstanding_value ?? 0
      );
    }
    //eslint-disable-next-line
  }, [formik.values.refund]);

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  return { formik, isCancelling, getFieldError };
};

export default useCancelSubscription;
